'use client';

import { SvgEuro } from '@icons/designSystemIcons/SvgEuro';
import { SvgInjection } from '@icons/designSystemIcons/SvgInjection';
import { SvgTimeLeft } from '@icons/designSystemIcons/SvgTimeLeft';
import { SvgTimer } from '@icons/designSystemIcons/SvgTimer';
import { SvgAngleDown } from '@icons/oldIcons/SvgAngleDown';
import { SvgCalendar } from '@icons/oldIcons/SvgCalendar';
import { priceFormat } from '@utils/priceFormat';
import { getDiscountedPrice } from '@utils/productCommon';
import { Quantifier } from 'app/(dashboard)/dashboard/(pages)/budgets/HighLightedProduct/Quantifier';
import {
  Operation,
  useCartStore,
} from 'app/(dashboard)/dashboard/(pages)/budgets/stores/userCartStore';
import DynamicIcon, {
  IconFamily,
} from 'app/(web)/components/common/DynamicIcon';
import RenderOnView from 'app/(web)/components/common/RenderOnView';
import { useSessionStore } from 'app/stores/globalStore';
import { CartItem, EmlaType, Product, ProductType } from 'app/types/product';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from 'designSystem/Accordion/Accordion';
import { Button } from 'designSystem/Buttons/Buttons';
import { Container, Flex } from 'designSystem/Layouts/Layouts';
import { Text, Title } from 'designSystem/Texts/Texts';
import { isEmpty } from 'lodash';
import dynamic from 'next/dynamic';

const PepperWidget = dynamic(() => import('./PepperWidget'));
const ProductSelectorButton = dynamic(() => import('./ProductSelectorButton'), {
  ssr: false,
});

// Textos del bloque que no salen de la BD tal como los pide el copy (GLP-1 no
// tiene sesiones en la BD: es una sesión al mes durante 6 meses).
const INFO_LABELS_BY_ID: Record<
  string,
  { title?: string; sessions?: string; time?: string; duration?: string }
> = {
  '668b678d-07ac-4431-8c44-4cf29d2d50c0': {
    title: 'Plan de pérdida de peso con GLP-1',
    sessions: '1 sesión mensual',
    time: '30 min',
    duration: '6 meses',
  },
};

export default function ProductInfo({
  product,
  className = '',
  isDashboard,
  hideAppliedProducts,
  hideDuration = false,
  hidePrice = false,
  disableCTA = false,
  disableClientData = true,
  ctaLanding = false,
}: {
  product: Product;
  className?: string;
  isDashboard?: boolean;
  hideAppliedProducts?: boolean;
  hideDuration?: boolean;
  hidePrice?: boolean;
  disableCTA?: boolean;
  disableClientData?: boolean;
  ctaLanding?: boolean;
}) {
  const { addItemToCart, getQuantityOfProduct, removeSingleProduct } =
    useCartStore(state => state);

  const { setBudgetId, currentOrigin } = useSessionStore(state => state);
  const sortedAndFilteredUpgrades = product?.upgrades
    ?.filter(upgrade => upgrade.product.type != ProductType.Voucher)
    .sort((a, b) => a.product.price - b.product.price);

  const uniqueProductsMap = new Map();
  sortedAndFilteredUpgrades?.forEach(upgrade => {
    if (!uniqueProductsMap.has(upgrade.product.title)) {
      uniqueProductsMap.set(upgrade.product.title, upgrade.product);
    }
  });

  const uniqueProducts: Product[] = Array.from(uniqueProductsMap.values());

  const { selectedClinic } = useSessionStore(state => state);
  const productDiscount = getDiscountedPrice(
    product,
    currentOrigin,
    selectedClinic?.id
  );

  const labels = product?.id
    ? INFO_LABELS_BY_ID[product.id.toLowerCase()]
    : undefined;
  const sessionsLabel = labels?.sessions;

  const formatDurationValue = (rawMonths: number) => {
    // 365 días daban "12.166666666666666 meses".
    const months = Math.round(rawMonths);
    if (months > 36 && months % 12 === 0) {
      return `${months / 12} años`;
    }
    return `${months} meses`;
  };

  const getDurationLabel = (durationMin: number, durationMax: number) => {
    if (durationMin < 30) {
      return 'Permanente';
    }

    const minMonths = durationMin / 30;
    const maxMonths = durationMax / 30;

    if (durationMax === durationMin) {
      return `Duración de ${formatDurationValue(minMonths)}`;
    }

    return `Duración de ${formatDurationValue(
      minMonths
    )} a ${formatDurationValue(maxMonths)}`;
  };

  return (
    <Container className={`${isDashboard ? 'px-0' : 'mb-12'} ${className}`}>
      {!process.env.NEXT_PUBLIC_CONFIG_DETAIL_ID?.includes(product.id) && (
        <div
          className={`w-full md:flex gap-8 justify-between items-start bg-hg-cream ${
            isDashboard ? 'rounded-2xl' : 'md:rounded-2xl'
          }`}
        >
          <Container className="py-6 md:px-6 md:flex md:flex-col md:justify-center md:items-start">
            {!isDashboard && (
              <Title size="2xl" className="font-bold mb-8 md:hidden">
                Tu tratamiento
              </Title>
            )}
            <ul className="flex flex-col pb-4 w-full mb-4">
              {!hideAppliedProducts && (
                <li className="mb-4 pb-4 border-b border-hg-black flex flex-col gap-2">
                  {!labels?.title && !isEmpty(product.appliedProducts) ? (
                    product.appliedProducts.map(item => {
                      const iconName = item.icon.split('/')[0] || 'SvgCross';
                      const iconFamily = (item.icon.split('/')[1] ||
                        'designSystemIcons') as IconFamily;

                      return (
                        <Flex key={item.titlte} className="items-start">
                          <DynamicIcon
                            className="mr-3 text-hg-secondary shrink-0 h-6 w-6"
                            name={iconName}
                            family={iconFamily}
                          />

                          <Text
                            className={`font-semibold ${
                              isDashboard ? '' : 'md:text-lg'
                            }`}
                          >
                            {item.titlte}
                          </Text>
                        </Flex>
                      );
                    })
                  ) : (
                    <Flex className="items-start">
                      <SvgInjection
                        height={24}
                        width={24}
                        className="mr-3 text-hg-secondary shrink-0"
                      />

                      <Text
                        className={`font-semibold ${
                          isDashboard ? '' : 'md:text-lg'
                        }`}
                      >
                        {labels?.title ?? product.description}
                      </Text>
                    </Flex>
                  )}
                </li>
              )}

              {(product.sessions > 0 || sessionsLabel) && (
                <li className="mb-4 pb-4 border-b border-hg-black flex">
                  <div
                    className={`flex relative md:justify-center flex-col w-full`}
                  >
                    {(product.sessions > 0 || sessionsLabel) && (
                      <>
                        <div className={`flex-1 flex items-start pr-4w-full`}>
                          <SvgTimeLeft
                            height={24}
                            width={24}
                            className="text-hg-secondary mr-3"
                          />
                          <Text
                            className={`font-semibold ${
                              isDashboard ? '' : 'md:text-lg'
                            }`}
                          >
                            {sessionsLabel ??
                              `${product.sessions} ${
                                product.sessions === 1 ? 'sesión' : 'sesiones'
                              }`}
                          </Text>
                        </div>
                      </>
                    )}
                  </div>
                </li>
              )}

              <li className="mb-4 pb-4 border-b border-hg-black flex">
                <SvgTimer
                  height={24}
                  width={24}
                  className="text-hg-secondary mr-3"
                />
                <div>
                  <Text
                    className={`font-semibold ${
                      isDashboard ? '' : 'md:text-lg'
                    }`}
                  >
                    {labels?.time ??
                      `${
                        product.emlaType === EmlaType.Required
                          ? product.applicationTimeMinutes * 2
                          : product.applicationTimeMinutes
                      } minutos`}
                  </Text>
                </div>
              </li>

              {!hideDuration && (
                <li className="mb-4 pb-4 border-b border-hg-black flex">
                  <SvgCalendar
                    height={24}
                    width={24}
                    className="text-hg-secondary mr-3"
                  />
                  <div className="flex flex-col">
                    <Text
                      className={`font-semibold ${
                        isDashboard ? '' : 'md:text-lg'
                      }`}
                    >
                      {labels?.duration ??
                        getDurationLabel(
                          product.durationMin,
                          product.durationMax
                        )}
                    </Text>
                  </div>
                </li>
              )}

              {!hidePrice && (
                <li className="flex">
                  <SvgEuro
                    height={24}
                    width={24}
                    className="text-hg-secondary mr-3"
                  />
                  <div className="flex flex-col">
                    {productDiscount != null && productDiscount > 0 ? (
                      <div className="flex gap-2">
                        <Text
                          className={`line-through opacity-50 ${
                            isDashboard ? '' : 'md:text-lg '
                          }`}
                        >
                          {priceFormat(product.price)} €
                        </Text>
                        <Text
                          className={`font-semibold ${
                            isDashboard ? '' : 'md:text-lg '
                          }`}
                        >
                          {priceFormat(productDiscount)} €
                        </Text>
                      </div>
                    ) : (
                      <Text
                        className={`font-semibold ${
                          isDashboard ? '' : 'md:text-lg '
                        }`}
                      >
                        {productDiscount && (
                          <span className="font-light line-through text-md text-hg-black500 mr-1">
                            {`${priceFormat(product.price)} €`}
                          </span>
                        )}
                        <span>
                          {priceFormat(
                            productDiscount ? productDiscount : product.price
                          )}{' '}
                          €
                        </span>
                      </Text>
                    )}
                  </div>
                </li>
              )}
            </ul>

            {!isDashboard && !disableCTA && (
              <>
                <ProductSelectorButton
                  product={product}
                  disableClientData={disableClientData}
                  ctaLanding={ctaLanding}
                />
                {product.price <= 2500 && (
                  <RenderOnView
                    className="w-full min-h-[85px] md:mt-5 mb-4 md:mb-0"
                    rootMargin="300px"
                  >
                    <PepperWidget price={product.price} />
                  </RenderOnView>
                )}
              </>
            )}

            {isDashboard && (
              <Flex className="bg-white flex rounded-full gap-2 p-2 pl-4 w-full justify-between">
                {productDiscount != null && productDiscount > 0 ? (
                  <div className="flex gap-2">
                    <span className="line-through opacity-50 text-hg-secondary text-xl">
                      {priceFormat(product.price)} €
                    </span>
                    <span className="font-semibold text-hg-secondary text-xl">
                      {priceFormat(productDiscount)} €
                    </span>
                  </div>
                ) : (
                  <>
                    <span className="font-semibold text-hg-secondary text-xl">
                      {priceFormat(product.price)} €
                    </span>
                  </>
                )}

                <Quantifier
                  handleUpdateQuantity={function handleUpdateQuantity(
                    operation: Operation
                  ): void {
                    if (operation == 'increase') {
                      addItemToCart(product as CartItem);
                    } else {
                      removeSingleProduct(product as CartItem);
                    }
                    setBudgetId('');
                  }}
                  quantity={getQuantityOfProduct(product)}
                />
              </Flex>
            )}
          </Container>
        </div>
      )}

      {process.env.NEXT_PUBLIC_CONFIG_DETAIL_ID!.includes(
        product.id.toLocaleLowerCase()
      ) && (
        <Accordion
          type="single"
          defaultValue="item-0"
          className={`flex flex-col w-full ${product.type === 2 ? 'gap-4' : ''}`}
        >
          {uniqueProducts.map((productConfig, index) => {
            const discountedPrice = getDiscountedPrice(
              productConfig,
              currentOrigin
            );

            return (
              <AccordionItem
                key={productConfig.title}
                value={`item-${index}`}
                className={`
                  overflow-hidden 
                  ${index === 0 ? ' rounded-t-2xl' : ''}
                  ${index !== 0 ? ' border-t-4 border-hg-cream300' : ''}
                `}
              >
                <AccordionTrigger>
                  <Flex className="w-full justify-between bg-derma-secondary500 p-4">
                    <Text className="md:text-lg text-left font-semibold">
                      {productConfig.title}
                    </Text>
                    <SvgAngleDown
                      height={24}
                      width={24}
                      className="transition-transform origin-center group-radix-state-open:rotate-180 group-radix-state-open:duration-200 shrink-0"
                    />
                  </Flex>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="md:flex gap-8 justify-between items-start bg-derma-secondary400 w-full">
                    <Container className="py-6 md:px-6 md:flex md:flex-col md:justify-center md:items-start">
                      <ul className="flex flex-col pb-4 w-full mb-4">
                        {productConfig.detail.length > 0 && (
                          <li className="mb-4 pb-4 flex text-sm text-hg-black500">
                            {productConfig.detail}
                          </li>
                        )}

                        <li className="mb-4 pb-4 border-b border-hg-black flex">
                          <Flex className="items-start">
                            <SvgInjection
                              height={24}
                              width={24}
                              className="mr-3 text-hg-secondary shrink-0"
                            />

                            <Text className="font-semibold md:text-lg">
                              {productConfig.appliedProducts
                                .map(x => x.titlte)
                                .join(' + ')}
                            </Text>
                          </Flex>
                        </li>
                        <li className="mb-4 pb-4 border-b border-hg-black flex">
                          <SvgTimer
                            height={24}
                            width={24}
                            className="text-hg-secondary mr-3"
                          />
                          <div>
                            <Text className="font-semibold md:text-lg">
                              {productConfig.applicationTimeMinutes} minutos
                            </Text>
                          </div>
                        </li>

                        <li className="mb-4 pb-4 border-b border-hg-black flex">
                          <SvgCalendar
                            height={24}
                            width={24}
                            className="text-hg-secondary mr-3"
                          />
                          <div className="flex flex-col">
                            <Text
                              className={`font-semibold ${
                                isDashboard ? '' : 'md:text-lg'
                              }`}
                            >
                              {getDurationLabel(
                                product.durationMin,
                                product.durationMax
                              )}
                            </Text>
                          </div>
                        </li>

                        <li className="flex">
                          <SvgEuro
                            height={24}
                            width={24}
                            className="text-hg-secondary mr-3"
                          />
                          <div className="flex flex-col">
                            <Text className="font-semibold md:text-lg flex gap-1.5 items-baseline">
                              <span>desde</span>

                              {discountedPrice && (
                                <span className="font-light line-through text-md text-hg-black500">
                                  {`${priceFormat(productConfig.price)} €`}
                                </span>
                              )}
                              <span>
                                {priceFormat(
                                  discountedPrice
                                    ? discountedPrice
                                    : productConfig.price
                                )}{' '}
                                €
                              </span>
                            </Text>
                          </div>
                        </li>
                      </ul>
                      <Button
                        size="lg"
                        type="primary"
                        className="mb-6 md:mb-0 md:mt-auto scroll-smooth"
                        id="tmevent_click_book_anchor_button"
                        href="#prices"
                      >
                        Me interesa
                      </Button>

                      {productConfig.price <= 2500 && (
                        <RenderOnView
                          className="w-full min-h-[85px] md:mt-5 mb-4 md:mb-0"
                          rootMargin="300px"
                        >
                          <PepperWidget price={productConfig.price} />
                        </RenderOnView>
                      )}
                    </Container>
                  </div>
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
      )}
    </Container>
  );
}
